import { useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { getApiErrorMessage } from "@/api/client";
import { FormButton } from "@/components/form/FormButton";
import { FormFieldError } from "@/components/form/FormField";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";
import { useIsPublishedDoc } from "@/utils/useIsPublishedDoc";

import type { Scope } from "@/models/document/DocumentPermissionsModel";

const Text = ({ className, children }: { className?: string; children: React.ReactNode }) => {
  return (
    <div
      className={twMerge("mb-1.25 px-1.25 font-geist text-size-[16px] leading-[1.2]", className)}
    >
      {children}
    </div>
  );
};

export const RequestPermissionPanel = observer(function RequestPermissionPanel({
  alwaysOpen = false,
}: {
  alwaysOpen?: boolean;
}) {
  const t = useTranslations();
  const { doc, classroom } = useStore();
  const isPublishedDoc = useIsPublishedDoc();
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { view } = useStore();

  if (!doc) {
    return null;
  }

  const showRequestViewButton =
    !isPublishedDoc &&
    !doc.publicRead &&
    !doc.permissions.read &&
    doc.permissions.requestScope !== "write" &&
    doc.permissions.requestScope !== "comment" &&
    doc.permissions.requestScope !== "read";
  const showRequestCommentButton =
    !showRequestViewButton &&
    !doc.publicRead &&
    doc.permissions.requestScope !== "comment" &&
    doc.permissions.requestScope !== "write" &&
    !doc.permissions.comment;
  const showRequestEditButton =
    !doc.publicWrite &&
    doc.permissions.requestScope !== "write" &&
    !doc.permissions.write &&
    !classroom.isStudentIn(doc.team);

  const isLocked = doc.locked;

  let publicOrClosedText = null;
  let yourPermissionsText = null;

  if (isLocked) {
    publicOrClosedText = t.rich("requestPermission.lockedDescription", {
      strong: (text) => <strong>{text}</strong>,
    });
  } else if (doc.publicRead && doc.publicWrite) {
    publicOrClosedText = t.rich("requestPermission.publicCanViewEditDescription", {
      strong: (text) => <strong>{text}</strong>,
    });
  } else if ((doc.publicRead && !doc.publicWrite) || isPublishedDoc) {
    publicOrClosedText = t.rich("requestPermission.publicCanViewDescription", {
      strong: (text) => <strong>{text}</strong>,
    });
  } else {
    publicOrClosedText = t.rich("requestPermission.closedDescription", {
      strong: (text) => <strong>{text}</strong>,
    });
  }

  if (doc.permissions.read && doc.permissions.write && doc.permissions.comment) {
    yourPermissionsText = t("requestPermission.canViewCommentEditDescription");
  } else if (doc.permissions.read && !doc.permissions.write && doc.permissions.comment) {
    yourPermissionsText = t("requestPermission.canViewCommentDescription");
  } else if (doc.permissions.read && !doc.permissions.write && !doc.permissions.comment) {
    yourPermissionsText = t("requestPermission.canViewDescription");
  }

  const handleRequestPermission = async (scope: Scope) => {
    try {
      setError("");
      setIsLoading(true);
      await doc.permissions.requestPermission(scope);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      data-panel="request_permission_panel"
      className="flex flex-1 flex-col max-w-[270px] mx-auto p-1.25"
    >
      <div className="flex flex-row items-center h-[34px] px-1.25">
        {!doc.publicRead && <Icon name="Lock" className="mr-2.5" />}
        {!doc.publicRead && doc.permissions.read && <Icon name="Shared" className="mr-2.5" />}
        {!doc.publicRead && doc.permissions.comment && <Icon name="Comment" className="mr-2.5" />}
        {(doc.permissions.write || doc.publicWrite) && <Icon name="Write" className="mr-2.5" />}
        {(doc.publicRead || doc.permissions.read) && !alwaysOpen && !isLocked && (
          <Sidebar.Button
            icon={
              <Icon
                name="ArrowDownLarge"
                className={twMerge(
                  "[&>path]:stroke-document-text",
                  view.isRequestPermissionPanelVisible && "-rotate-180",
                )}
              />
            }
            onClick={() => view.toggleRequestPermissionPanel()}
            className="ml-auto -mr-[5px] w-[34px] min-h-[34px]"
          />
        )}
      </div>
      {isLocked ? (
        <div className="flex flex-col mt-2.5">
          <Text>{publicOrClosedText}</Text>
        </div>
      ) : (
        (view.isRequestPermissionPanelVisible || alwaysOpen) && (
          <div className="flex flex-col mt-2.5">
            <Text>
              {publicOrClosedText}
              {!doc.publicRead &&
                !isPublishedDoc &&
                yourPermissionsText &&
                ` ${yourPermissionsText}`}
            </Text>
            {doc.permissions.read && (
              <Text className="mt-3.75">{t("requestPermission.youCanFork2")}</Text>
            )}
            {doc.permissions.requestScope === "write" &&
              !doc.permissions.write &&
              !doc.publicWrite && (
                <Text className="mt-3.75">{t("requestPermission.requestedEditPermission")}</Text>
              )}
            {doc.permissions.requestScope === "comment" && !doc.permissions.comment && (
              <Text className="mt-3.75">{t("requestPermission.requestedCommentPermission")}</Text>
            )}
            {doc.permissions.requestScope === "read" &&
              !doc.permissions.read &&
              !doc.publicRead && (
                <Text className="mt-3.75">{t("requestPermission.requestedViewPermission")}</Text>
              )}
            {showRequestViewButton && (
              <FormButton
                onClick={() => handleRequestPermission("read")}
                type="button"
                className="w-full text-xs h-8.5 tracking-tight rounded-8 mt-1.25"
                isBusy={isLoading}
              >
                {t("requestPermission.requestViewPermission")}
              </FormButton>
            )}
            {showRequestCommentButton && (
              <FormButton
                onClick={() => handleRequestPermission("comment")}
                type="button"
                className="w-full text-xs h-8.5 tracking-tight rounded-8 mt-1.25"
                isBusy={isLoading}
              >
                {t("requestPermission.requestCommentPermission")}
              </FormButton>
            )}
            {showRequestEditButton && (
              <FormButton
                onClick={() => handleRequestPermission("write")}
                type="button"
                className="w-full text-xs h-8.5 tracking-tight rounded-8 mt-1.25"
                isBusy={isLoading}
                variant="accent"
              >
                {t("requestPermission.requestEditPermission")}
              </FormButton>
            )}
            {error && <FormFieldError message={error} />}
          </div>
        )
      )}
    </div>
  );
});
